import { SearchRedirectSchema, calculateQuizScore, getQuizRecommendations } from "@garther/interfaces";
import { useI18n } from "@garther/ui/libs";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuthStore } from "@/features/auth/auth.store";
import { QuestionCard, QuizHeader, QuizHelp, QuizNavigation } from "@/features/quiz";
import { useCompleteQuiz, useMyQuizData, useQuizzes } from "@/features/quiz/hooks/use-quiz-query";
import { Layout } from "@/shared/components/layout";
import { trackQuizComplete, trackQuizStart } from "@/shared/libs/analytics";

export const Route = createFileRoute("/_auth/user/$id/quiz")({
  component: () => QuizPage(),
  validateSearch: SearchRedirectSchema.parse
});

function QuizPage() {
  const { id } = useParams({ from: "/_auth/user/$id/quiz" });
  const { redirectUrl } = useSearch({ from: "/_auth/user/$id/quiz" });

  const { curUser } = useAuthStore();
  const { t, currentLanguage } = useI18n();
  const isOwner = id === curUser?.fbUser?.uid;

  const { data: questions = [], isLoading } = useQuizzes();
  const { data: serverQuizData } = useMyQuizData({ enabled: isOwner });

  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const quizStartTracked = useRef(false);

  // Hydrate local answers from server data once it arrives
  useEffect(() => {
    if (serverQuizData?.answers) {
      setAnswers(serverQuizData.answers);
    }
  }, [serverQuizData]);

  // Track quiz start once
  useEffect(() => {
    if (isOwner && questions.length > 0 && !quizStartTracked.current) {
      quizStartTracked.current = true;
      trackQuizStart(id, "personality_quiz");
    }
  }, [isOwner, id, questions.length]);

  const completeQuizMutation = useCompleteQuiz({
    onSuccess: () => {
      const recommendations = getQuizRecommendations(answers);
      localStorage.setItem("quizRecommendations", JSON.stringify(recommendations));
      window.location.href = redirectUrl || `/user/${id}/profile`;
    }
  });

  const layout = useMemo(
    () => (children: React.ReactNode) => (
      <Layout>
        <div className="max-w-2xl mx-auto">{children}</div>
      </Layout>
    ),
    []
  );

  if (!isOwner) {
    return layout(
      <div className="text-center py-8">
        <h2 className="text-textcolor-primary text-xl font-bold mb-4">{t("quiz.page.accessDenied")}</h2>
        <p className="text-textcolor-secondary">{t("quiz.page.accessDeniedDescription")}</p>
      </div>
    );
  }

  if (isLoading && questions.length === 0) {
    return layout(
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-textcolor-secondary">{t("quiz.page.loadingQuiz")}</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return layout(
      <div className="text-center py-8">
        <h2 className="text-textcolor-primary text-xl font-bold mb-4">
          {t("quiz.page.noQuestionsAvailable")}
        </h2>
        <p className="text-textcolor-secondary">{t("quiz.page.noQuestionsDescription")}</p>
      </div>
    );
  }

  if (currentQuestion >= questions.length) {
    setCurrentQuestion(0);
    return layout(
      <div className="text-center py-8">
        <p className="text-textcolor-secondary">{t("quiz.page.loadingQuestion")}</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  if (!currentQ) {
    return layout(
      <div className="text-center py-8">
        <p className="text-textcolor-secondary">{t("quiz.page.questionNotFound")}</p>
      </div>
    );
  }

  const currentAnswer = answers[currentQ.id];

  const handleAnswer = (answer: any) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: answer }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const score = calculateQuizScore(answers);
    trackQuizComplete(id, "personality_quiz", score);
    completeQuizMutation.mutate({ answers });
  };

  const canProceed = () => {
    if (currentAnswer === null || currentAnswer === undefined) return false;

    if (currentQ.type === "single") {
      return typeof currentAnswer === "number" && currentAnswer >= 0;
    }

    if (currentQ.type === "multiple") {
      return (
        Array.isArray(currentAnswer) &&
        currentAnswer.length > 0 &&
        currentAnswer.every((item) => typeof item === "number" && item >= 0)
      );
    }

    if (currentQ.type === "scale" || currentQ.type === "degree") {
      return typeof currentAnswer === "number" && currentAnswer >= 0;
    }

    return true;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <QuizHeader currentQuestion={currentQuestion} totalQuestions={questions.length} t={t} />
        <QuestionCard
          question={currentQ}
          currentAnswer={currentAnswer}
          onAnswerChange={handleAnswer}
          t={t}
          language={currentLanguage}
        />
        <QuizNavigation
          currentQuestion={currentQuestion}
          totalQuestions={questions.length}
          canProceed={canProceed}
          isSubmitting={completeQuizMutation.isPending}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          t={t}
        />
        <QuizHelp t={t} />
      </div>
    </Layout>
  );
}
